import { Box, Stack, Text, Button, Group } from '@mantine/core'
import type { Icon } from '@tabler/icons-react'
import { WidgetCard } from './WidgetCard'
import { useCan } from '@/store/auth'

const ACCENT_COLORS = {
  primary: { brand: 'var(--ai-brand-verde)', glow: 'rgba(9,141,147,0.18)', glow2: 'rgba(2,84,104,0.06)', border: 'rgba(9,141,147,0.28)', shadow: 'rgba(9,141,147,0.10)' },
  info:    { brand: '#49b6d6', glow: 'rgba(73,182,214,0.18)', glow2: 'rgba(73,182,214,0.06)', border: 'rgba(73,182,214,0.28)', shadow: 'rgba(73,182,214,0.10)' },
  warning: { brand: '#f59c1a', glow: 'rgba(245,156,26,0.18)', glow2: 'rgba(245,156,26,0.06)', border: 'rgba(245,156,26,0.28)', shadow: 'rgba(245,156,26,0.10)' },
  danger:  { brand: '#ff5b57', glow: 'rgba(255,91,87,0.18)',  glow2: 'rgba(255,91,87,0.06)',  border: 'rgba(255,91,87,0.28)',  shadow: 'rgba(255,91,87,0.10)' },
  success: { brand: '#2f9e44', glow: 'rgba(47,158,68,0.18)',  glow2: 'rgba(47,158,68,0.06)',  border: 'rgba(47,158,68,0.28)',  shadow: 'rgba(47,158,68,0.10)' },
} as const

type AccentKey = keyof typeof ACCENT_COLORS
type SizeKey = 'sm' | 'md' | 'lg'

const SIZE_TOKENS: Record<SizeKey, {
  padding: string
  halo: number
  iconSize: number
  diamond1: number
  diamond2: number
  titleSize: string
  descSize: string
  descMaxWidth: number
  btnSize: 'xs' | 'sm'
  stackGap: string | number
  diamondOpacity1: number
  diamondOpacity2: number
}> = {
  lg: {
    padding: '64px 32px',
    halo: 76,
    iconSize: 36,
    diamond1: 280,
    diamond2: 180,
    titleSize: 'xl',
    descSize: 'sm',
    descMaxWidth: 520,
    btnSize: 'sm',
    stackGap: 'md',
    diamondOpacity1: 0.35,
    diamondOpacity2: 0.5,
  },
  md: {
    padding: '32px 24px',
    halo: 56,
    iconSize: 26,
    diamond1: 200,
    diamond2: 130,
    titleSize: 'lg',
    descSize: 'xs',
    descMaxWidth: 380,
    btnSize: 'xs',
    stackGap: 'sm',
    diamondOpacity1: 0.30,
    diamondOpacity2: 0.45,
  },
  sm: {
    padding: '16px 16px',
    halo: 40,
    iconSize: 20,
    diamond1: 120,
    diamond2: 80,
    titleSize: 'sm',
    descSize: 'xs',
    descMaxWidth: 280,
    btnSize: 'xs',
    stackGap: 6,
    diamondOpacity1: 0.25,
    diamondOpacity2: 0.4,
  },
}

export interface EmptyStateAction {
  label: string
  icon?: React.ReactNode
  onClick?: () => void
  href?: string
  /** Hide if user lacks permission. Uses useCan(resource, action). */
  permission?: { resource: string; action: string }
}

export interface EmptyStateProps {
  /** Tabler icon component (e.g. IconDatabaseOff). */
  icon: Icon
  /** Required headline. */
  title: string
  /** Supporting paragraph(s). May contain emphasized spans. */
  description?: React.ReactNode
  /** Filled brand button — typically the "configure / create" CTA. */
  primaryAction?: EmptyStateAction
  /** Default-style button — typically "refresh / try again". */
  secondaryAction?: EmptyStateAction
  /** Top-border accent + halo color. Defaults to `primary` (brand teal). */
  accent?: AccentKey
  /** Render without the WidgetCard wrapper (for embedding inside an existing card). */
  bare?: boolean
  /** Visual size: lg (full page), md (medium card), sm (compact box / chart). Default: lg. */
  size?: SizeKey
}

/**
 * Rich empty state for missing-config / no-data scenarios.
 *
 * Visual: WidgetCard with brand-accented top border, ambient diamond geometry,
 * halo icon, hierarchical title+copy, and up to two contextual actions.
 *
 * Sizes:
 * - `lg` (default) — full-page empty states (Minha Fila, no RDS, GitLab missing)
 * - `md` — medium cards (~250-400px tall): audit dashboard widgets, finops cards
 * - `sm` — compact boxes (~120-200px tall): chart placeholders, KPI strips
 *
 * Use for:
 * - CONFIG_MISSING: feature requires per-tenant setup
 * - EMPTY_LIST landings: top-level pages with a primary creation CTA
 * - Chart/widget placeholders awaiting data
 *
 * Skip for:
 * - STATUS_OK ("nenhum alerta") — celebratory
 * - NO_PERMISSION (use RestrictedFieldIndicator)
 */
export function EmptyState({
  icon: IconComponent,
  title,
  description,
  primaryAction,
  secondaryAction,
  accent = 'primary',
  bare = false,
  size = 'lg',
}: EmptyStateProps) {
  const can = useCan()
  const palette = ACCENT_COLORS[accent]
  const tokens = SIZE_TOKENS[size]

  const renderAction = (action: EmptyStateAction, variant: 'filled' | 'default') => {
    if (action.permission && !can(action.permission.resource, action.permission.action)) {
      return null
    }
    const commonProps = {
      size: tokens.btnSize,
      leftSection: action.icon,
      color: variant === 'filled' ? 'teal' : undefined,
      variant: variant === 'filled' ? undefined : 'default',
    }
    if (action.href) {
      return (
        <Button {...commonProps} component="a" href={action.href}>
          {action.label}
        </Button>
      )
    }
    return (
      <Button {...commonProps} onClick={action.onClick}>
        {action.label}
      </Button>
    )
  }

  const content = (
    <Box
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: tokens.padding,
        textAlign: 'center',
      }}
    >
      {/* Ambient geometric backdrop — diamonds rotated 45deg */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(45deg)',
          width: tokens.diamond1,
          height: tokens.diamond1,
          border: '1px solid var(--ai-border-light)',
          opacity: tokens.diamondOpacity1,
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(45deg)',
          width: tokens.diamond2,
          height: tokens.diamond2,
          border: '1px solid var(--ai-border-medium)',
          opacity: tokens.diamondOpacity2,
          pointerEvents: 'none',
        }}
      />

      <Stack align="center" gap={tokens.stackGap} style={{ position: 'relative' }}>
        {/* Icon halo */}
        <Box
          style={{
            width: tokens.halo,
            height: tokens.halo,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${palette.glow}, ${palette.glow2})`,
            border: `1px solid ${palette.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 0 24px ${palette.shadow}`,
          }}
        >
          <IconComponent size={tokens.iconSize} stroke={1.5} color={palette.brand} />
        </Box>

        <Stack gap={4} align="center" maw={tokens.descMaxWidth}>
          <Text size={tokens.titleSize} fw={600}>
            {title}
          </Text>
          {description && (
            <Text c="dimmed" size={tokens.descSize} style={{ lineHeight: 1.6 }}>
              {description}
            </Text>
          )}
        </Stack>

        {(primaryAction || secondaryAction) && (
          <Group gap="xs" mt={size === 'sm' ? 4 : 'xs'}>
            {secondaryAction && renderAction(secondaryAction, 'default')}
            {primaryAction && renderAction(primaryAction, 'filled')}
          </Group>
        )}
      </Stack>
    </Box>
  )

  if (bare) return content

  return (
    <WidgetCard accent={accent} p={0}>
      {content}
    </WidgetCard>
  )
}
